import { useQueryClient } from '@tanstack/react-query'
import { Alert, Card, Flex, Form, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useClassifierServiceGetIcd10AmClassifierElementListEndpoint,
  usePatientsServiceMarkAsPatientEndpoint,
  usePersonsServiceGetPersonByIdEndpointKey,
  useTreatmentServiceCreateConsultationExaminationEndpoint,
  useTreatmentServiceCreateConsultationInvestigationDemandLinkEndpoint,
  useTreatmentServiceCreatePotentialContactEndpoint,
  useTreatmentServiceCreateConsultationTreatmentPlanEndpoint,
  useTreatmentServiceGetConsultationEndpoint,
  useTreatmentServiceGetConsultationEndpointKey,
  useTreatmentServiceGetConsultationListEndpointKey,
  useTreatmentServiceGetLastExaminationEndpoint,
  useTreatmentServiceGetLastExaminationEndpointKey,
  useTreatmentServiceUpdateConsultationEndpoint,
  useTreatmentServiceUpdateConsultationExaminationEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  ConsultationStatus,
  ConsultationType,
  Permissions
} from 'src/api/requests'
import { DEFAULT_ONE_HUNDRED_PER_PAGE } from 'src/app-constants'
import { FormFooter, message, PageBreadcrumbs, Text } from 'src/components'
import {
  PersonConsultationsTreatmentSection,
  ConsultationGeneralInformationCard,
  ConsultationFinalDiagnosticCard,
  ConsultationFormCard,
  ActiveSimeTbTreatmentCard,
  ConsultationTbScreeningCard,
  DetailsLaboratoryCard,
  TreatmentDetails,
  tbScreeningToForm,
  tbScreeningToPayload
} from 'src/features/persons/components'
import {
  ContactsLayout,
  ContactsList,
  ContactsSection
} from 'src/features/persons/components/PersonalDetails/components/ContactsForm'
import { ConsultationLaboratoryCard } from 'src/features/persons/components/PersonConsultations/ConsultationLaboratoryCard'
import {
  normalizeCron,
  TreatmentInquiryItem
} from 'src/features/persons/components/PersonConsultations/Treatments'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { ValueLabelType } from 'src/features/persons/types'
import { assessmentToPayload, icd10Label } from 'src/features/persons/utils'
import {
  EXISTING_PREGNANCY_PLAN,
  EXISTING_TREATMENT_PLAN
} from 'src/features/persons/utils/constants'
import {
  useClassifierId,
  useScrollToFirstError,
  useUser,
  ValidationError
} from 'src/hooks'
import {
  formatDate,
  hourMinutesSecondFormat,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

export const EditConsultationPage = () => {
  const { t, currentLang } = useLocales()
  const { personId, id } = useParams()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const [form] = Form.useForm()
  const { onScrollToError } = useScrollToFirstError(form)
  const { hasPermission } = useUser()

  const [isContactsOpen, setIsContactsOpen] = useState(false)
  const onToggle = () => setIsContactsOpen((prev) => !prev)

  const hasExamination = Form.useWatch('hasExamination', form)

  const hasLaboratory = Form.useWatch('hasLaboratory', form)
  // Bumped by the TB screening to open the TB investigation request.
  const [tbRequestSignal, setTbRequestSignal] = useState(0)
  const [tbPreselectCategoryIds, setTbPreselectCategoryIds] = useState<
    string[]
  >([])
  const hasTreatment = Form.useWatch('hasTreatment', form)
  const hasConsultationType = !!Form.useWatch(
    ['generalInformation', 'consultationType'],
    form
  )
  const hasCheckedContacts = Form.useWatch('hasContacts', form)
  const hasContacts = !!Form.useWatch('contactsList', form)
  const hasFinalDiagnostic = Form.useWatch('hasFinalDiagnostic', form)

  const treatmentPlanId = form.getFieldValue(EXISTING_TREATMENT_PLAN)
  const pregnancyPlanId = form.getFieldValue(EXISTING_PREGNANCY_PLAN)

  const { breadcrumbItems, fullName, person, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const { data: consultation, isLoading: isLoadingConsultation } =
    useTreatmentServiceGetConsultationEndpoint({ id: String(id) }, [id], {
      enabled: !!id
    })

  // A performed consultation is edited (only by its author, on the day it was performed); a scheduled one is
  // performed through this page.
  const isPerformed = consultation?.status === ConsultationStatus.PRESENT
  const isEditLocked = isPerformed && !consultation?.canEdit
  const existingExaminationId = isPerformed
    ? consultation?.examinationId
    : undefined
  const hasExistingTreatmentPlan =
    isPerformed && !!consultation?.treatmentPlanId

  const { data: examination, isLoading: isLoadingExamination } =
    useTreatmentServiceGetLastExaminationEndpoint(
      { consultationId: String(id) },
      [id],
      { enabled: !!id && !!existingExaminationId }
    )

  const icd10ClassifierId = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  const coveredDiseaseIds =
    examination?.form?.anamnesisSection?.coveredDiseasesAndIllnessesIds ?? []

  const { data: coveredDiseases, isLoading: isLoadingCoveredDiseases } =
    useClassifierServiceGetIcd10AmClassifierElementListEndpoint(
      {
        id: String(icd10ClassifierId),
        ids: coveredDiseaseIds,
        pageNumber: 1,
        pageSize: DEFAULT_ONE_HUNDRED_PER_PAGE
      },
      undefined,
      {
        enabled:
          !!existingExaminationId &&
          !!icd10ClassifierId &&
          coveredDiseaseIds.length > 0
      }
    )

  const { mutateAsync: updateExamination } =
    useTreatmentServiceUpdateConsultationExaminationEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutate, isPending } = useTreatmentServiceUpdateConsultationEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: async (data, payload) => {
      message.success(
        t(
          isPerformed
            ? 'consultations.success_update'
            : 'consultations.success_create'
        )
      )
      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetConsultationListEndpointKey]
      })
      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetConsultationEndpointKey]
      })

      if (hasTreatment) await updateTarvCode()

      const formData = form.getFieldsValue(true)

      const createFormPayload = {
        form: {
          ...formData.form,
          anamnesisSection: {
            ...formData?.form?.anamnesisSection,
            familyMedicalAnamnesis:
              formData?.form?.anamnesisSection?.familyMedicalAnamnesis ?? {},
            substanceConsumption:
              formData?.form?.anamnesisSection?.substanceConsumption ?? {},
            vaccines: formData?.form?.anamnesisSection?.vaccines ?? {},
            surgeriesInAnamnesis:
              formData?.form?.anamnesisSection?.surgeriesInAnamnesis ||
              undefined,
            allergies: formData?.form?.anamnesisSection?.allergies ?? {},
            currentMedication:
              formData?.form?.anamnesisSection?.currentMedication || undefined,
            reproductiveAndSexualHealth:
              formData?.form?.anamnesisSection?.reproductiveAndSexualHealth ??
              {},
            pregnancy: formData?.form?.anamnesisSection?.pregnancy ?? {},
            coveredDiseasesAndIllnessesIds:
              formData?.form?.anamnesisSection?.coveredDiseasesAndIllnessesIds?.map(
                (item: ValueLabelType) => item.value
              ) ?? []
          },
          physicalExaminationSection: {
            ...formData?.form?.physicalExaminationSection,
            constitutionalType:
              formData?.form?.physicalExaminationSection?.constitutionalType ??
              {},
            cutaneousTeguments:
              formData?.form?.physicalExaminationSection?.cutaneousTeguments ??
              {},
            oropharynx:
              formData?.form?.physicalExaminationSection?.oropharynx ?? {},
            genitalAnalRegionAssessment:
              formData?.form?.physicalExaminationSection
                ?.genitalAnalRegionAssessment ?? {}
          },
          examinationDate: dayjs(
            `${payload.requestBody.generalInformation.consultationDate} ${payload.requestBody.generalInformation.consultationHour}`
          ).format()
        }
      }

      // Editing replaces the existing examination instead of adding a second one.
      if (hasExamination && existingExaminationId) {
        try {
          await updateExamination({
            id: existingExaminationId,
            requestBody: createFormPayload
          })
        } catch {
          // The error (e.g. the edit period has expired) is shown by the mutation; stay on the page.
          return
        }
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetLastExaminationEndpointKey]
        })
      } else if (hasExamination) {
        createConsultationForm({
          requestBody: {
            ...createFormPayload,
            consultationId: String(data.id),
            consultationType: payload.requestBody.generalInformation
              .consultationType as ConsultationType
          }
        })
      }

      const investigationDemandIds = form.getFieldValue(
        'investigationDemandIds'
      )

      if (!treatmentPlanId && hasTreatment) {
        const values = await form.validateFields()

        const medicine = values?.medicine.map((item: TreatmentInquiryItem) => ({
          internationalNameId: item?.internationalNameId,
          productFormId: item?.productFormId?.id,
          amount: item?.amount,
          scheduleExpression: normalizeCron(item?.cron),
          startDate: dayjs(item?.startDate).format(yearMonthDaySlash),
          endDate: toDayjs(item?.endDate)?.format(yearMonthDaySlash)
        }))

        addTreatment({
          id: String(data.id),
          requestBody: {
            treatmentTypeId: values?.treatment_type,
            startDate: formatDate(
              values?.treatment_start_date,
              yearMonthDaySlash
            ),
            endDate: toDayjs(values?.treatment_end_date)?.format(
              yearMonthDaySlash
            ),
            prescribedTreatments: medicine,
            isUrgent: values?.isUrgent,
            treatmentTypeRegimenId: values?.treatmentTypeRegimenId
          }
        })

        if (!person?.simeCode && !person?.tarvCode) {
          addPacient({
            id: String(personId),
            requestBody: {
              simeCode: values?.simeCode,
              tarvCode: values?.tarvCode
            }
          })

          return navigate(
            `${paths.patients.details(personId)}${paths.patients.consultations.root()}`
          )
        }
      }

      if (hasLaboratory && investigationDemandIds?.length > 0) {
        addLaboratory({
          id: String(data.id),
          requestBody: { investigationDemandIds }
        })
      }

      if (hasContacts) {
        const { contactsList } = await form.validateFields()

        createContact({
          consultationId: String(data.id),
          requestBody: { contacts: contactsList }
        })
      }
      navigate(
        `${paths.patients.details(personId)}${paths.patients.consultations.root()}`
      )
    }
  })

  const { mutateAsync: addTreatment } =
    useTreatmentServiceCreateConsultationTreatmentPlanEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutate: addLaboratory } =
    useTreatmentServiceCreateConsultationInvestigationDemandLinkEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutateAsync: addPacient } = usePatientsServiceMarkAsPatientEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors)
  })

  const { mutateAsync: createConsultationForm } =
    useTreatmentServiceCreateConsultationExaminationEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutateAsync: updatePatientCodes } =
    usePatientsServiceMarkAsPatientEndpoint()

  // TARV code is editable in the treatment section - persist it on the patient
  // without failing the consultation save
  const updateTarvCode = async () => {
    const tarvCode = String(form.getFieldValue('tarvCode') ?? '').trim()

    if (!person?.simeCode || tarvCode === (person.tarvCode ?? '')) return

    try {
      await updatePatientCodes({
        id: String(personId),
        requestBody: { simeCode: person.simeCode, tarvCode: tarvCode || null }
      })
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetPersonByIdEndpointKey]
      })
    } catch {
      message.error(t('common.tarv_code_update_error'))
    }
  }

  const { mutate: createContact } =
    useTreatmentServiceCreatePotentialContactEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const onBackClick = () => {
    navigate('..', { relative: 'path' })
  }

  const onSave = async () => {
    try {
      const values = await form.validateFields()

      const payload = {
        personId: String(personId),
        generalInformation: {
          consultationType: values.generalInformation.consultationType,
          consultationDate: formatDate(
            values.generalInformation.consultationDate,
            yearMonthDaySlash
          ),
          consultationHour: dayjs
            .utc(values.generalInformation.consultationHour)
            .format(hourMinutesSecondFormat),
          lastConsultationDate: form.getFieldValue('lastConsultationDate')
        },
        // A single diagnosis: the preliminary information is no longer sent.
        assessment: assessmentToPayload(values.assessment),
        ...(treatmentPlanId && {
          existingTreatmentPlanId: treatmentPlanId
        }),
        ...(pregnancyPlanId && {
          existingPregnancyMonitoringId: pregnancyPlanId
        }),
        tbScreening: tbScreeningToPayload(values.tbScreening),
        form: undefined
      }

      mutate({
        id: String(id),

        requestBody: payload
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  useEffect(() => {
    if (!consultation) return

    const generalInformation = {
      consultationDate: toDayjs(
        consultation.generalInformation?.consultationDate
      ),
      consultationHour: toDayjs(
        consultation.generalInformation?.consultationDate
      )
    }

    if (consultation.status !== ConsultationStatus.PRESENT) {
      form.setFieldsValue({
        generalInformation,
        tbScreening: tbScreeningToForm(consultation.tbScreening)
      })

      return
    }

    // Editing a performed consultation: the whole form comes from it (never from the latest consultation).
    const { assessment } = consultation

    form.setFieldsValue({
      generalInformation: {
        ...generalInformation,
        consultationType:
          consultation.generalInformation?.consultationType ?? undefined
      },
      lastConsultationDate:
        consultation.generalInformation?.lastConsultationDate ?? undefined,
      tbScreening: tbScreeningToForm(consultation.tbScreening),
      hasExamination: !!consultation.examinationId,
      hasFinalDiagnostic: !!assessment,
      assessment: assessment
        ? {
            comments: assessment.comments ?? undefined,
            dateOfDiseaseOnset: toDayjs(assessment.dateOfDiseaseOnset),
            aidsClinicalCategoryId:
              assessment.aidsClinicalCategoryId ?? undefined,
            aidsClinicalConditionIds: assessment.aidsClinicalConditionIds
              ?.length
              ? assessment.aidsClinicalConditionIds
              : undefined,
            opportunisticDiseaseIds: assessment.opportunisticDiseases?.map(
              (item) => ({
                value: item.id,
                label: item.translation?.[currentLang?.value]
              })
            ),
            finalDiagnosticIds: assessment.finalDiagnostics?.map((item) => ({
              value: item.id,
              label: icd10Label(
                item.code,
                item.translation?.[currentLang?.value]
              )
            }))
          }
        : undefined,
      hasTreatment: !!consultation.treatmentPlanId,
      [EXISTING_TREATMENT_PLAN]: consultation.treatmentPlanId ?? undefined,
      [EXISTING_PREGNANCY_PLAN]: consultation.pregnancyMonitoringId ?? undefined
    })
  }, [consultation])

  // The examination form of the edited consultation, with the covered diseases labelled like the select.
  useEffect(() => {
    if (!existingExaminationId || !examination?.form) return
    if (coveredDiseaseIds.length > 0 && !coveredDiseases) return

    const { anamnesisSection } = examination.form

    form.setFieldsValue({
      form: {
        ...examination.form,
        anamnesisSection: anamnesisSection && {
          ...anamnesisSection,
          coveredDiseasesAndIllnessesIds: coveredDiseaseIds.map((diseaseId) => {
            const disease = coveredDiseases?.data?.find(
              (item) => item.id === diseaseId
            )

            return {
              value: diseaseId,
              label: icd10Label(
                disease?.code,
                disease?.translation?.[currentLang?.value]
              )
            }
          })
        }
      }
    })
  }, [existingExaminationId, examination, coveredDiseases])

  return (
    <Spin
      spinning={
        isLoadingPerson ||
        isLoadingConsultation ||
        (!!existingExaminationId &&
          (isLoadingExamination ||
            (coveredDiseaseIds.length > 0 && isLoadingCoveredDiseases)))
      }
    >
      <PageBreadcrumbs title={fullName} items={breadcrumbItems} showBack>
        <Flex vertical gap={24}>
          <Flex justify="space-between">
            <Text className="modal-title">
              {t(
                isPerformed
                  ? 'consultations.edit_consultation'
                  : 'consultations.manage_consultation'
              )}
            </Text>
          </Flex>

          {isPerformed && (
            <Alert
              showIcon
              type={isEditLocked ? 'error' : 'info'}
              message={t(
                isEditLocked
                  ? 'consultations.edit_period_expired'
                  : 'consultations.edit_until_end_of_day'
              )}
            />
          )}

          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
            scrollToFirstError={true}
          >
            <Flex vertical gap={24}>
              {/* Mounted once the consultation is known, so a performed one skips the latest-consultation prefill. */}
              {consultation && (
                <Card>
                  <ConsultationGeneralInformationCard
                    form={form}
                    isEditingPage
                    skipLatestPrefill={isPerformed}
                  />
                </Card>
              )}

              <ActiveSimeTbTreatmentCard
                personId={personId}
                idnp={person?.idnp}
              />

              <Card>
                <ConsultationTbScreeningCard
                  form={form}
                  personId={personId}
                  onRequestLaboratory={(categoryIds) => {
                    form.setFieldValue('hasLaboratory', true)
                    setTbPreselectCategoryIds(categoryIds)
                    setTbRequestSignal((signal) => signal + 1)
                  }}
                />
              </Card>

              {hasConsultationType && hasExamination && (
                <Card>
                  <ConsultationFormCard form={form} />
                </Card>
              )}

              {isPerformed &&
                !!consultation?.investigationDemandsIds?.length && (
                  <DetailsLaboratoryCard
                    investigationDemandsIds={
                      consultation.investigationDemandsIds
                    }
                  />
                )}

              {hasLaboratory && (
                <ConsultationLaboratoryCard
                  isVisible={!!hasLaboratory}
                  form={form}
                  openTbRequestSignal={tbRequestSignal}
                  tbPreselectCategoryIds={tbPreselectCategoryIds}
                />
              )}

              {hasFinalDiagnostic && (
                <Card>
                  <ConsultationFinalDiagnosticCard form={form} />
                </Card>
              )}

              {/* An existing treatment plan is shown read-only: editing the consultation never creates another one. */}
              {hasTreatment && hasExistingTreatmentPlan && (
                <Flex vertical gap={16}>
                  <Alert
                    showIcon
                    type="info"
                    message={t('consultations.treatment_plan_read_only')}
                  />
                  <TreatmentDetails data={consultation} />
                </Flex>
              )}

              {hasTreatment && !hasExistingTreatmentPlan && (
                <PersonConsultationsTreatmentSection
                  person={person}
                  form={form}
                />
              )}

              {hasPermission(
                Permissions.TREATMENT_CONSULTATION_VIEW_POTENTIAL_CONTACTS_INSTITUTION
              ) &&
                hasCheckedContacts && (
                  <Card>
                    <ContactsLayout onToggle={onToggle}>
                      <Form.Item name="contactsList">
                        <ContactsList form={form} />
                      </Form.Item>
                    </ContactsLayout>
                  </Card>
                )}
              {isContactsOpen && (
                <ContactsSection onCancel={onToggle} form={form} />
              )}
            </Flex>

            <FormFooter
              onCancel={onBackClick}
              onSave={onSave}
              saveConfirmTitle={`${t('persons.save')}?`}
              saveLabel={t('persons.save')}
              loading={isPending}
              disabled={isEditLocked}
            />
          </Form>
        </Flex>
      </PageBreadcrumbs>
    </Spin>
  )
}
